import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as TreeIcon,
  ViewKanban as KanbanIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import {
  errorMessage,
  fetchBoards, createBoard, updateBoard, deleteBoard,
  createList, updateList, deleteList, reorderLists,
  fetchBoardTasks, createBoardTask, updateBoardTask, deleteBoardTask, reorderBoardTasks,
} from 'shared/utils/projectBoardApi';
import KanbanBoard from './KanbanBoard';
import TaskTree from './TaskTree';
import BoardTaskDialog from './BoardTaskDialog';

const MENU_PROPS = { MenuProps: { sx: { '&.MuiMenu-root': { zIndex: '10001 !important' } } } };

const storageGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const storageSet = (key, value) => { try { localStorage.setItem(key, value); } catch { /* ignore */ } };

const NameDialog = ({ open, title, label, initial, initialDescription, withDescription, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => {
    if (open) { setName(initial || ''); setDescription(initialDescription || ''); }
  }, [open, initial, initialDescription]);
  const submit = () => name.trim() && onSubmit({ name: name.trim(), description });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth label={label} value={name} sx={{ mt: 1 }}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !withDescription) submit(); }} />
        {withDescription && (
          <TextField fullWidth multiline minRows={2} label="Description" value={description} sx={{ mt: 2 }}
            onChange={(e) => setDescription(e.target.value)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!name.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Project management for a proposal or publication: kanban boards with
 * coordinator-defined lists, plus a hierarchical view of the same tasks.
 *
 * members: [{ id, name, email }]; sections: [{ id, title }].
 * canManage: coordinator / project leader — may add, rename, reorder and delete boards and lists.
 */
const ProjectManagementPanel = ({ proposalId, members = [], sections = [], canManage = false, currentUserId }) => {
  const { showSnackbar } = useSnackbar();
  const boardKey = `pm-board:${proposalId}`;
  const viewKey = `pm-view:${proposalId}`;

  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [view, setView] = useState(() => storageGet(viewKey) || 'kanban');
  const [onlyMine, setOnlyMine] = useState(false);
  const [hideDone, setHideDone] = useState(false);

  const [taskDialog, setTaskDialog] = useState({ open: false, task: null, defaults: null });
  const [nameDialog, setNameDialog] = useState(null); // { kind: 'newBoard' | 'renameBoard' | 'renameList', target }
  const [confirm, setConfirm] = useState(null); // { title, text, action }
  const [listToDelete, setListToDelete] = useState(null);
  const [moveTo, setMoveTo] = useState('');
  const [boardMenu, setBoardMenu] = useState(null);

  const fail = useCallback((err, fallback) => showSnackbar(errorMessage(err, fallback), 'error'), [showSnackbar]);

  const board = boards.find((b) => b.id === boardId) || null;
  const lists = useMemo(
    () => [...(board?.lists || [])].sort((a, b) => a.position - b.position),
    [board],
  );

  const loadBoards = useCallback(async (selectId) => {
    try {
      const data = await fetchBoards(proposalId);
      setBoards(data);
      setBoardId((prev) => {
        const wanted = selectId || prev || storageGet(boardKey);
        return data.some((b) => b.id === wanted) ? wanted : data[0]?.id || null;
      });
    } catch (err) {
      fail(err, 'Failed to load boards');
    } finally {
      setLoadingBoards(false);
    }
  }, [proposalId, boardKey, fail]);

  const loadTasks = useCallback(async () => {
    if (!boardId) { setTasks([]); return; }
    setLoadingTasks(true);
    try {
      setTasks(await fetchBoardTasks(boardId));
    } catch (err) {
      fail(err, 'Failed to load tasks');
    } finally {
      setLoadingTasks(false);
    }
  }, [boardId, fail]);

  useEffect(() => { if (proposalId) loadBoards(); }, [proposalId, loadBoards]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { if (boardId) storageSet(boardKey, boardId); }, [boardId, boardKey]);
  useEffect(() => { storageSet(viewKey, view); }, [view, viewKey]);

  const visibleTaskIds = useMemo(() => new Set(
    tasks
      .filter((t) => !hideDone || !t.is_done)
      .filter((t) => !onlyMine || (t.assignees || []).map(String).includes(String(currentUserId)))
      .map((t) => t.id),
  ), [tasks, hideDone, onlyMine, currentUserId]);

  const replaceTask = (updated) => setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));

  // ---- Boards --------------------------------------------------------------
  const handleNameSubmit = async ({ name, description }) => {
    const { kind, target } = nameDialog;
    try {
      if (kind === 'newBoard') {
        const created = await createBoard({ proposal: proposalId, name, description });
        await loadBoards(created.id);
      } else if (kind === 'renameBoard') {
        await updateBoard(target.id, { name, description });
        await loadBoards();
      } else if (kind === 'renameList') {
        await updateList(target.id, { name });
        await loadBoards();
      }
      setNameDialog(null);
    } catch (err) {
      fail(err, 'Could not save');
    }
  };

  const handleDeleteBoard = () => setConfirm({
    title: `Delete board “${board.name}”?`,
    text: 'All lists and tasks on this board will be permanently deleted.',
    action: async () => {
      await deleteBoard(board.id);
      setBoardId(null);
      await loadBoards();
    },
  });

  // ---- Lists ---------------------------------------------------------------
  const handleAddList = async (name) => {
    try {
      await createList({ board: boardId, name });
      await loadBoards();
    } catch (err) { fail(err, 'Could not add list'); }
  };

  const handleToggleDoneList = async (list) => {
    try {
      await updateList(list.id, { is_done_list: !list.is_done_list });
      await loadBoards();
    } catch (err) { fail(err, 'Could not update list'); }
  };

  const handleReorderLists = async (ids) => {
    setBoards((prev) => prev.map((b) => (b.id !== boardId ? b : {
      ...b, lists: b.lists.map((l) => ({ ...l, position: ids.indexOf(l.id) + 1 })),
    })));
    try { await reorderLists(boardId, ids); } catch (err) { fail(err, 'Could not reorder lists'); loadBoards(); }
  };

  const requestDeleteList = (list) => {
    const count = tasks.filter((t) => t.board_list === list.id).length;
    if (count === 0) {
      setConfirm({
        title: `Delete list “${list.name}”?`,
        text: 'The list is empty.',
        action: async () => { await deleteList(list.id); await loadBoards(); },
      });
      return;
    }
    setMoveTo(lists.find((l) => l.id !== list.id)?.id || '');
    setListToDelete({ ...list, count });
  };

  const confirmDeleteList = async () => {
    try {
      await deleteList(listToDelete.id, moveTo);
      setListToDelete(null);
      await Promise.all([loadBoards(), loadTasks()]);
    } catch (err) { fail(err, 'Could not delete list'); }
  };

  // ---- Tasks ---------------------------------------------------------------
  const handleSaveTask = async (data) => {
    try {
      if (taskDialog.task) {
        replaceTask(await updateBoardTask(taskDialog.task.id, data));
      } else {
        const created = await createBoardTask(data);
        setTasks((prev) => [...prev, created]);
      }
      setTaskDialog({ open: false, task: null, defaults: null });
    } catch (err) { fail(err, 'Could not save task'); }
  };

  const handleQuickAdd = async (listId, title) => {
    try {
      const created = await createBoardTask({ board_list: listId, title });
      setTasks((prev) => [...prev, created]);
    } catch (err) { fail(err, 'Could not add task'); }
  };

  const handleMoveTask = async (taskId, listId, orderedIds) => {
    const destList = lists.find((l) => l.id === listId);
    setTasks((prev) => prev.map((t) => {
      const idx = orderedIds.indexOf(t.id);
      if (idx === -1) return t;
      const moved = t.board_list !== listId;
      return { ...t, board_list: listId, position: idx + 1, ...(moved ? { is_done: !!destList?.is_done_list } : {}) };
    }));
    try { await reorderBoardTasks(listId, orderedIds); } catch (err) { fail(err, 'Could not move task'); loadTasks(); }
  };

  // Ticking a task done in the tree also moves it into the board's "done" list
  // (and back out when unticked) so both views stay consistent.
  const handleToggleDone = async (task) => {
    const isDone = !task.is_done;
    const doneList = lists.find((l) => l.is_done_list);
    const currentList = lists.find((l) => l.id === task.board_list);
    let targetList = task.board_list;
    if (isDone && doneList) targetList = doneList.id;
    if (!isDone && currentList?.is_done_list) targetList = lists.find((l) => !l.is_done_list)?.id || task.board_list;
    replaceTask({ ...task, is_done: isDone, board_list: targetList });
    try {
      replaceTask(await updateBoardTask(task.id, { is_done: isDone, board_list: targetList }));
    } catch (err) { fail(err, 'Could not update task'); loadTasks(); }
  };

  const handleMoveToList = async (task, listId) => {
    if (task.board_list === listId) return;
    try { replaceTask(await updateBoardTask(task.id, { board_list: listId })); } catch (err) { fail(err, 'Could not move task'); }
  };

  const handleDeleteTask = (task) => {
    const subCount = tasks.filter((t) => t.parent === task.id).length;
    setConfirm({
      title: `Delete “${task.title}”?`,
      text: subCount ? 'Its subtasks will be deleted as well.' : 'This cannot be undone.',
      action: async () => {
        await deleteBoardTask(task.id);
        setTaskDialog({ open: false, task: null, defaults: null });
        await loadTasks();
      },
    });
  };

  const runConfirm = async () => {
    try {
      await confirm.action();
      setConfirm(null);
    } catch (err) { fail(err, 'Action failed'); }
  };

  const openNewTask = (defaults = null) => setTaskDialog({ open: true, task: null, defaults });
  const canDeleteTask = (task) => canManage || String(task?.created_by) === String(currentUserId);

  // ---- Render --------------------------------------------------------------
  if (loadingBoards) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ px: 2, py: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {boards.length > 0 && (
              <TextField select size="small" label="Board" value={boardId || ''} onChange={(e) => setBoardId(e.target.value)}
                sx={{ minWidth: 220 }} SelectProps={MENU_PROPS}>
                {boards.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </TextField>
            )}
            {canManage && board && (
              <IconButton onClick={(e) => setBoardMenu(e.currentTarget)} aria-label="Board options"><MoreIcon /></IconButton>
            )}
            {canManage && (
              <Button startIcon={<AddIcon />} onClick={() => setNameDialog({ kind: 'newBoard' })}>New board</Button>
            )}
          </Stack>

          <Box sx={{ flex: 1 }} />

          {board && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControlLabel control={<Switch size="small" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />} label="My tasks" />
              <FormControlLabel control={<Switch size="small" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />} label="Hide completed" />
              <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => v && setView(v)}>
                <ToggleButton value="kanban"><Tooltip title="Kanban board"><KanbanIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="tree"><Tooltip title="Task hierarchy"><TreeIcon fontSize="small" /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewTask()} disabled={lists.length === 0}>
                Add task
              </Button>
            </Stack>
          )}
        </Stack>
        {board?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{board.description}</Typography>
        )}
      </Paper>

      {!board ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No boards yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {canManage
              ? 'Create a board to plan the work. Each new board starts with To Do, In Progress and Done lists, which you can change.'
              : 'The project coordinator has not created a board yet.'}
          </Typography>
          {canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNameDialog({ kind: 'newBoard' })}>Create board</Button>
          )}
        </Paper>
      ) : loadingTasks && tasks.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : view === 'kanban' ? (
        <KanbanBoard
          lists={lists}
          tasks={tasks}
          visibleTaskIds={visibleTaskIds}
          canManage={canManage}
          onOpenTask={(task) => setTaskDialog({ open: true, task, defaults: null })}
          onQuickAdd={handleQuickAdd}
          onMoveTask={handleMoveTask}
          onReorderLists={handleReorderLists}
          onAddList={handleAddList}
          onRenameList={(list) => setNameDialog({ kind: 'renameList', target: list })}
          onToggleDoneList={handleToggleDoneList}
          onDeleteList={requestDeleteList}
        />
      ) : (
        <TaskTree
          lists={lists}
          tasks={tasks}
          visibleTaskIds={visibleTaskIds}
          currentUserId={currentUserId}
          canManage={canManage}
          onOpenTask={(task) => setTaskDialog({ open: true, task, defaults: null })}
          onAddSubtask={(parent) => openNewTask({ parent: parent.id, board_list: parent.board_list })}
          onToggleDone={handleToggleDone}
          onMoveToList={handleMoveToList}
          onDeleteTask={handleDeleteTask}
        />
      )}

      <Menu anchorEl={boardMenu} open={Boolean(boardMenu)} onClose={() => setBoardMenu(null)} sx={{ zIndex: 10001 }}>
        <MenuItem onClick={() => { setNameDialog({ kind: 'renameBoard', target: board }); setBoardMenu(null); }}>Rename board</MenuItem>
        <MenuItem onClick={() => { handleDeleteBoard(); setBoardMenu(null); }} sx={{ color: 'error.main' }}>Delete board</MenuItem>
      </Menu>

      <BoardTaskDialog
        open={taskDialog.open}
        task={taskDialog.task}
        defaults={taskDialog.defaults}
        lists={lists}
        tasks={tasks}
        members={members}
        sections={sections}
        canDelete={canDeleteTask(taskDialog.task)}
        onClose={() => setTaskDialog({ open: false, task: null, defaults: null })}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
      />

      <NameDialog
        open={Boolean(nameDialog)}
        title={nameDialog?.kind === 'newBoard' ? 'New board' : nameDialog?.kind === 'renameBoard' ? 'Edit board' : 'Rename list'}
        label={nameDialog?.kind === 'renameList' ? 'List name' : 'Board name'}
        initial={nameDialog?.target?.name}
        initialDescription={nameDialog?.target?.description}
        withDescription={nameDialog?.kind !== 'renameList'}
        onClose={() => setNameDialog(null)}
        onSubmit={handleNameSubmit}
      />

      <Dialog open={Boolean(listToDelete)} onClose={() => setListToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete list “{listToDelete?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            It holds {listToDelete?.count} task(s). Choose where to move them.
          </DialogContentText>
          <TextField select fullWidth label="Move tasks to" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} SelectProps={MENU_PROPS}>
            {lists.filter((l) => l.id !== listToDelete?.id).map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListToDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteList} disabled={!moveTo}>Move & delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent><DialogContentText>{confirm?.text}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={runConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectManagementPanel;
